import { BidsAsks } from '../types/orderbook';
import { Gwei } from './gwei';
import { divide, multiply } from './math';
import { calcMarginRate, CalcMaxPositionParams, calcTotalCostPerUnit } from './maxPositionSize';
import { calcPriceImpactBasedOnSize } from './orderbookHelpers';
import { PrettyMerge } from './tsUtilityTypes';

/**
 * Computes the maximum quantity limit based on availableMargin and margin requirements,
 * ignoring the price impact (just uses oraclePrice). Returns 0 if no margin is available.
 */
function computeQuantityLimit(
  params: PrettyMerge<CalcMaxMarketOrderParams, { availableMargin: bigint; oraclePrice: bigint }>
) {
  const { availableMargin, feeRate, initialMarginRatio, maxQuantity, oraclePrice } = params;

  const initialMarginRatioBigInt = Gwei.toGwei(initialMarginRatio);
  const marginRate = calcMarginRate(initialMarginRatioBigInt, feeRate);

  // Max notional ignoring price impact
  const maxNotionalWithoutImpact = divide(availableMargin, marginRate);

  // Calculate initial max quantity based on the oraclePrice
  const initialMaxQuantity = divide(maxNotionalWithoutImpact, oraclePrice);
  const quantityLimit = Gwei.min(initialMaxQuantity, maxQuantity);

  return { initialMarginRatioBigInt, quantityLimit };
}

function checkMinimumLotSize(params: {
  availableMargin: bigint;
  bidsAsks: BidsAsks;
  side: 'long' | 'short';
  lotSize: bigint;
  oraclePrice: bigint;
  feeRate: bigint;
  initialMarginRatioBigInt: bigint;
}) {
  const {
    availableMargin,
    bidsAsks,
    side,
    lotSize,
    oraclePrice,
    feeRate,
    initialMarginRatioBigInt,
  } = params;

  // First, check if there's enough liquidity for the minimum lot
  const minSizeResult = calcPriceImpactBasedOnSize(bidsAsks, side, lotSize);
  if (!minSizeResult.executionPrice || !minSizeResult.enoughLiquidity) {
    return { canAffordMinSize: false };
  }

  // Calculate price impact difference
  let impactDiff =
    side === 'long'
      ? minSizeResult.executionPrice - oraclePrice
      : oraclePrice - minSizeResult.executionPrice;
  if (impactDiff < 0n) {
    impactDiff = 0n;
  }
  const marginRate = calcMarginRate(initialMarginRatioBigInt, feeRate);
  // Calculate margin needed for the minimum lot
  const minSizeCostPerUnit = calcTotalCostPerUnit({
    priceDifference: impactDiff,
    markPrice: minSizeResult.executionPrice,
    marginRate,
  });
  const minSizeMarginNeeded = multiply(lotSize, minSizeCostPerUnit);

  // Check if the user has enough margin for the minimum lot
  const canAffordMinSize = minSizeMarginNeeded <= availableMargin;
  return { canAffordMinSize };
}

/**
 * Performs a binary search to find the largest affordable size up to `quantityLimit`.
 */
function findMaxAffordableSize(params: {
  availableMargin: bigint;
  bidsAsks: BidsAsks;
  side: 'long' | 'short';
  lotSize: bigint;
  oraclePrice: bigint;
  feeRate: bigint;
  initialMarginRatioBigInt: bigint;
  quantityLimit: bigint;
}): bigint {
  const {
    availableMargin,
    bidsAsks,
    side,
    lotSize,
    oraclePrice,
    feeRate,
    initialMarginRatioBigInt,
    quantityLimit,
  } = params;

  let low = lotSize;
  let high = quantityLimit;
  let bestSize = 0n;

  while (low <= high) {
    // If the range is too small, check the low end as a final attempt
    if (high - low < lotSize * 2n) {
      if (bestSize > 0n) {
        return bestSize;
      }

      // Check if we can afford 'low' in a last-resort scenario
      const lowImpact = calcPriceImpactBasedOnSize(bidsAsks, side, low);
      if (lowImpact.executionPrice && lowImpact.enoughLiquidity) {
        let impactDiff =
          side === 'long'
            ? lowImpact.executionPrice - oraclePrice
            : oraclePrice - lowImpact.executionPrice;
        if (impactDiff < 0n) {
          impactDiff = 0n;
        }
        const marginRate = calcMarginRate(initialMarginRatioBigInt, feeRate);
        const lowCostPerUnit = calcTotalCostPerUnit({
          priceDifference: impactDiff,
          markPrice: lowImpact.executionPrice,
          marginRate,
        });

        const lowMarginNeeded = multiply(low, lowCostPerUnit);
        if (lowMarginNeeded <= availableMargin) {
          return low;
        }
      }
      return bestSize;
    }

    // Compute mid in terms of multiple of lotSize
    const lotsBetween = divide(high - low, lotSize);
    const halfLots = divide(lotsBetween, Gwei.toGwei(2));
    const mid = low + multiply(halfLots, lotSize);

    // Check execution price & liquidity
    const { executionPrice, enoughLiquidity } = calcPriceImpactBasedOnSize(bidsAsks, side, mid);
    if (!executionPrice || !enoughLiquidity) {
      high = mid - lotSize;
      continue;
    }

    // Calculate the upward price impact for a long (or downward for a short)
    let impactDiff = side === 'long' ? executionPrice - oraclePrice : oraclePrice - executionPrice;
    if (impactDiff < 0n) {
      impactDiff = 0n;
    }
    const marginRate = calcMarginRate(initialMarginRatioBigInt, feeRate);
    // Full per-unit cost
    const totalCostPerUnit = calcTotalCostPerUnit({
      priceDifference: impactDiff,
      markPrice: executionPrice,
      marginRate,
    });

    // Check if we can afford this mid size
    const totalMarginNeededForMid = multiply(mid, totalCostPerUnit);

    if (totalMarginNeededForMid <= availableMargin) {
      // mid is affordable; record it and try bigger sizes
      bestSize = mid;
      low = mid + lotSize;
    } else {
      // mid is too expensive; try smaller
      high = mid - lotSize;
    }
  }

  return bestSize;
}

interface CalcMaxMarketOrderParams
  extends Omit<CalcMaxPositionParams, 'reduceOnly' | 'currentPositionSize'> {
  bidsAsks: BidsAsks;
}
/**
 * Wrapper for the entire calculation
 */
export function calcMaxSizeForMarketOrder(params: CalcMaxMarketOrderParams): bigint | undefined {
  const { availableMargin, oraclePrice, bidsAsks } = params;

  if (!availableMargin || !oraclePrice || !bidsAsks) {
    return undefined;
  }

  // --- Step 1: Compute margin rates & quantity limit ---
  const { initialMarginRatioBigInt, quantityLimit } = computeQuantityLimit({
    ...params,
    availableMargin,
    oraclePrice,
    bidsAsks,
  });
  if (quantityLimit === 0n) {
    return 0n;
  }

  // --- Step 2: Check affordability of the minimum lot size ---
  const checkMinSizeResult = checkMinimumLotSize({
    ...params,
    initialMarginRatioBigInt,
    availableMargin,
    oraclePrice,
    bidsAsks,
  });
  // If the minimum lot size is not affordable or there's no liquidity, return 0n.
  if (!checkMinSizeResult.canAffordMinSize) {
    return 0n;
  }

  // --- Step 3: Perform binary search for the largest affordable size ---
  return findMaxAffordableSize({
    ...params,
    initialMarginRatioBigInt,
    quantityLimit,
    availableMargin,
    oraclePrice,
    bidsAsks,
  });
}
